import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
} from "@mui/material";
import { SelectBox } from "devextreme-react";
import Popup from "devextreme-react/popup";
import { showNotification } from "./NotificationProvider";

import { useDispatch, useSelector } from "react-redux";
import {
  getAllTeams,
  getTeamPeriods,
  getPeriodDetails,
  clearSelectedPeriod,
} from "../store";

const TeamPaymentHistory = () => {
  const dispatch = useDispatch();
  const teams = useSelector((state) => state.team.teams);
  const teamPeriods = useSelector((state) => state.teamPayment.teamPeriods);
  const selectedPeriod = useSelector(
    (state) => state.teamPayment.selectedPeriod
  );
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [periodDetailsPopupVisible, setPeriodDetailsPopupVisible] =
    useState(false);

  useEffect(() => {
    // Fetch teams if not already loaded
    if (teams.length === 0) {
      dispatch(getAllTeams());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTeamId) {
      fetchTeamPeriods(selectedTeamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId]);

  const fetchTeamPeriods = async (teamId) => {
    try {
      await dispatch(getTeamPeriods(teamId)).unwrap();
    } catch (error) {
      console.error("Failed to fetch team periods:", error);
      showNotification("Error", "error", "Failed to fetch payment history.");
    }
  };

  const handleViewDetails = async (periodId) => {
    try {
      await dispatch(getPeriodDetails(periodId)).unwrap();
      setPeriodDetailsPopupVisible(true);
    } catch (error) {
      console.error("Failed to fetch period details:", error);
      showNotification("Error", "error", "Failed to fetch period details.");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "#00E396";
      case "active":
        return "#FFC107";
      case "overdue":
        return "#F44336";
      default:
        return "#757575";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "#00E396";
      case "pending":
        return "#FFC107";
      case "canceled":
        return "#F44336";
      default:
        return "#757575";
    }
  };

  const renderPeriodDetailsPopup = useCallback(
    () => (
      <div style={{ padding: "1rem" }}>
        {selectedPeriod && (
          <>
            <div
              style={{
                backgroundColor: "#3A4663",
                padding: "1.5rem",
                borderRadius: "8px",
                marginBottom: "2rem",
              }}
            >
              <h3 style={{ color: "#E0E6ED", marginBottom: "1rem" }}>
                Period Summary
              </h3>
              <div style={{ color: "#E0E6ED", fontSize: "1.2rem" }}>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Period:</strong>{" "}
                  {new Date(
                    selectedPeriod.year,
                    selectedPeriod.month - 1
                  ).toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Required Amount:</strong> $
                  {selectedPeriod.requiredAmount}
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Paid Amount:</strong>{" "}
                  <span style={{ color: "#00E396" }}>
                    ${selectedPeriod.paidAmount}
                  </span>
                </div>
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong>Remaining:</strong>{" "}
                  <span style={{ color: "#FF9800" }}>
                    ${selectedPeriod.remainingAmount}
                  </span>
                </div>
                <div>
                  <strong>Status:</strong>{" "}
                  <Chip
                    label={selectedPeriod.status.toUpperCase()}
                    sx={{
                      backgroundColor: getStatusColor(selectedPeriod.status),
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                </div>
              </div>
            </div>

            <h3 style={{ color: "#E0E6ED", marginBottom: "1rem" }}>
              Payments ({selectedPeriod.payments.length})
            </h3>

            {selectedPeriod.payments.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#757575",
                }}
              >
                No payments yet for this period.
              </div>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: "#2E3B55",
                  maxHeight: "400px",
                  overflow: "auto",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ backgroundColor: "#3A4663", color: "#E0E6ED" }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "#3A4663", color: "#E0E6ED" }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "#3A4663", color: "#E0E6ED" }}
                      >
                        Submitted By
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "#3A4663", color: "#E0E6ED" }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "#3A4663", color: "#E0E6ED" }}
                      >
                        Notes
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPeriod.payments.map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell
                          sx={{ color: "#FF9800", fontWeight: "bold" }}
                        >
                          ${payment.amount}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.status.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: getPaymentStatusColor(
                                payment.status
                              ),
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#E0E6ED" }}>
                          {payment.createdBy.name} {payment.createdBy.lastName}
                          <br />
                          <span
                            style={{ fontSize: "0.9rem", color: "#9E9E9E" }}
                          >
                            {payment.createdBy.email}
                          </span>
                        </TableCell>
                        <TableCell sx={{ color: "#E0E6ED" }}>
                          {new Date(payment.createdAt).toLocaleString()}
                          {payment.approvedAt && (
                            <>
                              <br />
                              <span
                                style={{ fontSize: "0.9rem", color: "#00E396" }}
                              >
                                Approved:{" "}
                                {new Date(payment.approvedAt).toLocaleString()}
                              </span>
                            </>
                          )}
                          {payment.rejectedAt && (
                            <>
                              <br />
                              <span
                                style={{ fontSize: "0.9rem", color: "#F44336" }}
                              >
                                Rejected:{" "}
                                {new Date(payment.rejectedAt).toLocaleString()}
                              </span>
                            </>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: "#E0E6ED" }}>
                          {payment.notes || "-"}
                          {payment.rejectionReason && (
                            <>
                              <br />
                              <span
                                style={{ fontSize: "0.9rem", color: "#F44336" }}
                              >
                                Reason: {payment.rejectionReason}
                              </span>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </div>
    ),
    [selectedPeriod]
  );

  return (
    <div
      style={{
        padding: "2rem",
        backgroundColor: "#1C273A",
        minHeight: "100vh",
      }}
    >
      <div style={{ marginBottom: "2rem" }}>
        <h2
          style={{ color: "#E0E6ED", fontSize: "2rem", marginBottom: "1rem" }}
        >
          Team Payment History
        </h2>
        <div style={{ maxWidth: "400px" }}>
          <label
            style={{
              color: "#E0E6ED",
              fontSize: "1.3rem",
              marginBottom: "0.5rem",
              display: "block",
            }}
          >
            Select Team
          </label>
          <SelectBox
            items={teams}
            displayExpr="teamName"
            valueExpr="_id"
            value={selectedTeamId}
            onValueChanged={(e) => setSelectedTeamId(e.value)}
            placeholder="Choose a team"
            searchEnabled={true}
            width="100%"
          />
        </div>
      </div>

      {!selectedTeamId ? (
        <div
          style={{
            textAlign: "center",
            padding: "4rem",
            backgroundColor: "#2E3B55",
            borderRadius: "8px",
          }}
        >
          <span style={{ color: "#757575", fontSize: "1.5rem" }}>
            Please select a team to view payment history.
          </span>
        </div>
      ) : teamPeriods.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "4rem",
            backgroundColor: "#2E3B55",
            borderRadius: "8px",
          }}
        >
          <span style={{ color: "#757575", fontSize: "1.5rem" }}>
            No payment history found for this team.
          </span>
        </div>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "#2E3B55",
            color: "#E0E6ED",
          }}
        >
          <Table
            sx={{
              "& .MuiTableCell-root": {
                fontSize: "1.2rem",
                color: "#E0E6ED",
                borderBottom: "1px solid #3A4663",
              },
              "& .MuiTableHead-root": {
                backgroundColor: "#3A4663",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Required Amount</TableCell>
                <TableCell>Paid Amount</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payments Count</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamPeriods.map((period) => (
                <TableRow key={period._id}>
                  <TableCell>
                    <span style={{ fontWeight: "bold" }}>
                      {new Date(
                        period.year,
                        period.month - 1
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ color: "#2196F3", fontWeight: "bold" }}>
                      ${period.requiredAmount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ color: "#00E396", fontWeight: "bold" }}>
                      ${period.paidAmount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span style={{ color: "#FF9800", fontWeight: "bold" }}>
                      ${period.remainingAmount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={period.status.toUpperCase()}
                      sx={{
                        backgroundColor: getStatusColor(period.status),
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${period.payments.length} payments`}
                      sx={{
                        backgroundColor: "#556CD6",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleViewDetails(period._id)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Period Details Popup */}
      <Popup
        width={900}
        height={700}
        showTitle={true}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={periodDetailsPopupVisible}
        onHiding={() => {
          setPeriodDetailsPopupVisible(false);
          dispatch(clearSelectedPeriod());
        }}
        showCloseButton={true}
        container=".dx-viewport"
        title={
          selectedPeriod
            ? `Payment Details - ${new Date(
                selectedPeriod.year,
                selectedPeriod.month - 1
              ).toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}`
            : "Payment Details"
        }
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
        contentRender={renderPeriodDetailsPopup}
      />
    </div>
  );
};

export default TeamPaymentHistory;




